import React, { useState } from 'react';
import { SearchIcon, FilterIcon, ChevronDownIcon, ChevronUpIcon, MessageSquareIcon, BookmarkIcon, BellIcon, XIcon, TrophyIcon, CheckCircleIcon } from 'lucide-react';
interface Goal {
  id: number;
  title: string;
  category: string;
  progress: number;
  lastUpdate: string;
  lastUpdateNote: string;
  needsNudge: boolean;
  smartFramework: {
    specific: string;
    measurable: string;
    achievable: string;
    relevant: string;
    timeBound: string;
  };
  prideFramework: {
    personal: string;
    relational: string;
    intentional: string;
    dynamic: string;
    empowered: string;
  };
}
interface Mentee {
  id: number;
  name: string;
  image: string;
  startDate: string;
  goals: Goal[];
}
const MentorGoals: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFilter, setSelectedFilter] = useState<'all' | 'inProgress' | 'completed'>('all');
  const [expandedMentee, setExpandedMentee] = useState<number | null>(null);
  const [showGoalDetails, setShowGoalDetails] = useState<{
    menteeId: number;
    goalId: number;
  } | null>(null);
  const [showBadgeNotification, setShowBadgeNotification] = useState(true);
  const [showResourceModal, setShowResourceModal] = useState<{
    menteeId: number;
    goalId: number;
  } | null>(null);
  const mentees: Mentee[] = [{
    id: 1,
    name: 'Jordan Lee',
    image: 'https://randomuser.me/api/portraits/women/33.jpg',
    startDate: 'Jan 15, 2024',
    goals: [{
      id: 1,
      title: 'Build UX Portfolio',
      category: 'Career',
      progress: 65,
      lastUpdate: '2 days ago',
      lastUpdateNote: 'Completed first case study draft',
      needsNudge: false,
      smartFramework: {
        specific: 'Create a UX portfolio with 3-5 case studies',
        measurable: 'Complete 3-5 detailed case studies',
        achievable: 'Working on one case study at a time',
        relevant: 'Required for job applications',
        timeBound: 'Complete by end of Q1 2024'
      },
      prideFramework: {
        personal: 'Showcase my growth and skills',
        relational: 'Connect with potential employers',
        intentional: 'Career advancement in UX',
        dynamic: 'Iterative improvement based on feedback',
        empowered: 'Building confidence in my work'
      }
    }, {
      id: 2,
      title: 'Learn Figma Advanced Features',
      category: 'Skills',
      progress: 30,
      lastUpdate: '1 week ago',
      lastUpdateNote: 'Completed auto-layout tutorials',
      needsNudge: true,
      smartFramework: {
        specific: 'Master advanced Figma features',
        measurable: 'Complete advanced tutorial series',
        achievable: '1 hour daily practice',
        relevant: 'Essential for UX work',
        timeBound: 'Complete by February 2024'
      },
      prideFramework: {
        personal: 'Enhance technical skills',
        relational: 'Better collaboration with designers',
        intentional: 'Stay current with industry tools',
        dynamic: 'Regular practice and learning',
        empowered: 'Confidence in technical skills'
      }
    }]
  }, {
    id: 2,
    name: 'Casey Kim',
    image: 'https://randomuser.me/api/portraits/nonbinary/12.jpg',
    startDate: 'Dec 1, 2023',
    goals: [{
      id: 1,
      title: 'Develop Leadership Skills',
      category: 'Professional Development',
      progress: 45,
      lastUpdate: '3 days ago',
      lastUpdateNote: 'Led first team meeting',
      needsNudge: false,
      smartFramework: {
        specific: 'Develop core leadership capabilities',
        measurable: 'Lead 2 team projects',
        achievable: 'Taking on incremental responsibilities',
        relevant: 'Career growth goal',
        timeBound: 'Evaluate progress monthly'
      },
      prideFramework: {
        personal: 'Grow as a leader',
        relational: 'Better team management',
        intentional: 'Career advancement',
        dynamic: 'Adaptable leadership style',
        empowered: 'Confidence in leadership'
      }
    }]
  }];
  const GoalDetailsModal = ({
    menteeId,
    goalId
  }: {
    menteeId: number;
    goalId: number;
  }) => {
    const mentee = mentees.find(m => m.id === menteeId);
    const goal = mentee?.goals.find(g => g.id === goalId);
    if (!goal) return null;
    return <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" data-id="element-1211">
        <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" data-id="element-1212">
          <div className="p-6" data-id="element-1213">
            <div className="flex justify-between items-start mb-6" data-id="element-1214">
              <div data-id="element-1215">
                <h3 className="text-xl font-semibold" data-id="element-1216">{goal.title}</h3>
                <p className="text-sm text-gray-600" data-id="element-1217">{mentee?.name}'s Goal</p>
              </div>
              <button onClick={() => setShowGoalDetails(null)} data-id="element-1218">
                <XIcon size={20} className="text-gray-500" data-id="element-1219" />
              </button>
            </div>
            <div className="mb-6" data-id="element-1220">
              <div className="flex justify-between items-center mb-2" data-id="element-1221">
                <span className="text-sm font-medium" data-id="element-1222">Progress</span>
                <span className="text-sm font-medium text-purple-600" data-id="element-1223">
                  {goal.progress}%
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2" data-id="element-1224">
                <div className="bg-purple-600 rounded-full h-2 transition-all duration-300" style={{
                width: `${goal.progress}%`
              }} data-id="element-1225" />
              </div>
              <p className="text-sm text-gray-600 mt-2" data-id="element-1226">
                Last updated {goal.lastUpdate}: {goal.lastUpdateNote}
              </p>
            </div>
            <div className="grid grid-cols-2 gap-6" data-id="element-1227">
              <div data-id="element-1228">
                <h4 className="font-medium mb-3" data-id="element-1229">SMART Framework</h4>
                <div className="space-y-3" data-id="element-1230">
                  {Object.entries(goal.smartFramework).map(([key, value]) => <div key={key} className="bg-gray-50 p-3 rounded-lg" data-id="element-1231">
                      <span className="text-xs font-medium text-gray-500 block capitalize" data-id="element-1232">
                        {key}
                      </span>
                      <span className="text-sm" data-id="element-1233">{value}</span>
                    </div>)}
                </div>
              </div>
              <div data-id="element-1234">
                <h4 className="font-medium mb-3" data-id="element-1235">PRIDE Framework</h4>
                <div className="space-y-3" data-id="element-1236">
                  {Object.entries(goal.prideFramework).map(([key, value]) => <div key={key} className="bg-gray-50 p-3 rounded-lg" data-id="element-1237">
                      <span className="text-xs font-medium text-gray-500 block capitalize" data-id="element-1238">
                        {key}
                      </span>
                      <span className="text-sm" data-id="element-1239">{value}</span>
                    </div>)}
                </div>
              </div>
            </div>
            <div className="flex justify-end space-x-3 mt-6" data-id="element-1240">
              <button onClick={() => setShowGoalDetails(null)} className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700" data-id="element-1241">
                Close
              </button>
              <button onClick={() => {
              setShowResourceModal({
                menteeId,
                goalId
              });
              setShowGoalDetails(null);
            }} className="px-4 py-2 bg-purple-600 text-white rounded-lg" data-id="element-1242">
                Recommend Resources
              </button>
            </div>
          </div>
        </div>
      </div>;
  };
  const ResourceRecommendationModal = ({
    menteeId,
    goalId
  }: {
    menteeId: number;
    goalId: number;
  }) => {
    const mentee = mentees.find(m => m.id === menteeId);
    const goal = mentee?.goals.find(g => g.id === goalId);
    if (!goal) return null;
    const recommendedResources = [{
      id: 1,
      title: 'UX Portfolio Best Practices',
      type: 'Article',
      category: goal.category,
      description: 'Learn how to structure and present your UX case studies effectively.'
    }, {
      id: 2,
      title: 'Portfolio Review Workshop',
      type: 'Video',
      category: goal.category,
      description: 'Expert feedback on UX portfolios and common mistakes to avoid.'
    }];
    return <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" data-id="element-1243">
        <div className="bg-white rounded-xl w-full max-w-lg" data-id="element-1244">
          <div className="p-6" data-id="element-1245">
            <div className="flex justify-between items-start mb-6" data-id="element-1246">
              <div data-id="element-1247">
                <h3 className="text-lg font-semibold" data-id="element-1248">Recommend Resources</h3>
                <p className="text-sm text-gray-600" data-id="element-1249">
                  For {mentee?.name}'s goal: {goal.title}
                </p>
              </div>
              <button onClick={() => setShowResourceModal(null)} data-id="element-1250">
                <XIcon size={20} className="text-gray-500" data-id="element-1251" />
              </button>
            </div>
            <div className="space-y-4 mb-6" data-id="element-1252">
              {recommendedResources.map(resource => <div key={resource.id} className="border rounded-lg p-4" data-id="element-1253">
                  <div className="flex justify-between items-start" data-id="element-1254">
                    <div data-id="element-1255">
                      <h4 className="font-medium" data-id="element-1256">{resource.title}</h4>
                      <p className="text-sm text-gray-600" data-id="element-1257">
                        {resource.description}
                      </p>
                      <div className="flex items-center mt-2" data-id="element-1258">
                        <span className="text-xs bg-purple-100 text-purple-600 px-2 py-0.5 rounded-full" data-id="element-1259">
                          {resource.type}
                        </span>
                        <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full ml-2" data-id="element-1260">
                          {resource.category}
                        </span>
                      </div>
                    </div>
                    <button className="text-purple-600 hover:text-purple-700" data-id="element-1261">
                      <BookmarkIcon size={20} data-id="element-1262" />
                    </button>
                  </div>
                </div>)}
            </div>
            <div className="flex justify-end space-x-3" data-id="element-1263">
              <button onClick={() => setShowResourceModal(null)} className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700" data-id="element-1264">
                Cancel
              </button>
              <button onClick={() => setShowResourceModal(null)} className="px-4 py-2 bg-purple-600 text-white rounded-lg" data-id="element-1265">
                Send Recommendations
              </button>
            </div>
          </div>
        </div>
      </div>;
  };
  const sendCheckInMessage = (menteeId: number, goalId: number) => {
    console.log(`Sending check-in message for goal ${goalId} to mentee ${menteeId}`);
  };
  const sendNudge = (menteeId: number, goalId: number) => {
    console.log(`Sending nudge for goal ${goalId} to mentee ${menteeId}`);
  };
  const filteredMentees = mentees.filter(mentee => {
    const matchesSearch = mentee.name.toLowerCase().includes(searchQuery.toLowerCase()) || mentee.goals.some(goal => goal.title.toLowerCase().includes(searchQuery.toLowerCase()));
    if (selectedFilter === 'all') return matchesSearch;
    if (selectedFilter === 'completed') return matchesSearch && mentee.goals.some(goal => goal.progress === 100);
    if (selectedFilter === 'inProgress') return matchesSearch && mentee.goals.some(goal => goal.progress < 100);
    return matchesSearch;
  });
  return <div className="pb-4" data-id="element-1266">
      {showBadgeNotification && <div className="bg-purple-50 border-l-4 border-purple-600 p-4 mb-6 flex justify-between items-center" data-id="element-1267">
          <div className="flex items-center" data-id="element-1268">
            <TrophyIcon size={20} className="text-purple-600 mr-2" data-id="element-1269" />
            <div data-id="element-1270">
              <p className="font-medium text-purple-800" data-id="element-1271">
                New Achievement Unlocked!
              </p>
              <p className="text-sm text-purple-600" data-id="element-1272">
                You've helped 5 mentees complete their goals
              </p>
            </div>
          </div>
          <button onClick={() => setShowBadgeNotification(false)} data-id="element-1273">
            <XIcon size={20} className="text-purple-600" data-id="element-1274" />
          </button>
        </div>}
      <div className="mb-6" data-id="element-1275">
        <h1 className="text-xl font-bold mb-2" data-id="element-1276">Your Mentees' Goals</h1>
        <div className="flex items-center space-x-4" data-id="element-1277">
          <div className="flex-1 relative" data-id="element-1278">
            <SearchIcon size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" data-id="element-1279" />
            <input type="text" placeholder="Search by mentee or goal..." className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-1280" />
          </div>
          <select className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500" value={selectedFilter} onChange={e => setSelectedFilter(e.target.value as 'all' | 'inProgress' | 'completed')} data-id="element-1281">
            <option value="all" data-id="element-1282">All Goals</option>
            <option value="inProgress" data-id="element-1283">In Progress</option>
            <option value="completed" data-id="element-1284">Completed</option>
          </select>
        </div>
      </div>
      <div className="space-y-6" data-id="element-1285">
        {filteredMentees.map(mentee => <div key={mentee.id} className="bg-white rounded-xl shadow-sm overflow-hidden" data-id="element-1286">
            <div className="p-4 flex items-center justify-between cursor-pointer" onClick={() => setExpandedMentee(expandedMentee === mentee.id ? null : mentee.id)} data-id="element-1287">
              <div className="flex items-center" data-id="element-1288">
                <img src={mentee.image} alt={mentee.name} className="w-10 h-10 rounded-full object-cover" data-id="element-1289" />
                <div className="ml-3" data-id="element-1290">
                  <h3 className="font-medium" data-id="element-1291">{mentee.name}</h3>
                  <p className="text-sm text-gray-500" data-id="element-1292">
                    Mentorship started {mentee.startDate}
                  </p>
                </div>
              </div>
              {expandedMentee === mentee.id ? <ChevronUpIcon size={20} className="text-gray-400" data-id="element-1293" /> : <ChevronDownIcon size={20} className="text-gray-400" data-id="element-1294" />}
            </div>
            {expandedMentee === mentee.id && <div className="border-t border-gray-100 p-4 space-y-4" data-id="element-1295">
                {mentee.goals.map(goal => <div key={goal.id} className="bg-gray-50 rounded-lg p-4" data-id="element-1296">
                    <div className="flex justify-between items-start mb-3" data-id="element-1297">
                      <div data-id="element-1298">
                        <h4 className="font-medium" data-id="element-1299">{goal.title}</h4>
                        <span className="text-xs bg-purple-100 text-purple-600 px-2 py-0.5 rounded-full" data-id="element-1300">
                          {goal.category}
                        </span>
                      </div>
                      <div className="flex items-center space-x-2" data-id="element-1301">
                        <button onClick={() => sendCheckInMessage(mentee.id, goal.id)} className="p-1 text-purple-600 hover:bg-purple-100 rounded" title="Send check-in message" data-id="element-1302">
                          <MessageSquareIcon size={18} data-id="element-1303" />
                        </button>
                        <button onClick={() => setShowGoalDetails({
                  menteeId: mentee.id,
                  goalId: goal.id
                })} className="text-xs bg-purple-600 text-white px-3 py-1 rounded-lg" data-id="element-1304">
                          View Details
                        </button>
                      </div>
                    </div>
                    <div className="mb-3" data-id="element-1305">
                      <div className="flex justify-between items-center mb-1" data-id="element-1306">
                        <span className="text-sm text-gray-600" data-id="element-1307">Progress</span>
                        <span className="text-sm font-medium" data-id="element-1308">
                          {goal.progress}%
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2" data-id="element-1309">
                        <div className="bg-purple-600 rounded-full h-2 transition-all duration-300" style={{
                  width: `${goal.progress}%`
                }} data-id="element-1310" />
                      </div>
                    </div>
                    <div className="flex justify-between items-center text-sm" data-id="element-1311">
                      <span className="text-gray-500" data-id="element-1312">
                        Last update: {goal.lastUpdate}
                      </span>
                      {goal.needsNudge && <button onClick={() => sendNudge(mentee.id, goal.id)} className="flex items-center text-purple-600 hover:text-purple-700" data-id="element-1313">
                          <BellIcon size={16} className="mr-1" data-id="element-1314" />
                          Send Nudge
                        </button>}
                    </div>
                  </div>)}
              </div>}
          </div>)}
        {filteredMentees.length === 0 && <div className="text-center py-6 text-gray-500" data-id="element-1315">
            No goals found matching your search
          </div>}
      </div>
      {showGoalDetails && <GoalDetailsModal menteeId={showGoalDetails.menteeId} goalId={showGoalDetails.goalId} data-id="element-1316" />}
      {showResourceModal && <ResourceRecommendationModal menteeId={showResourceModal.menteeId} goalId={showResourceModal.goalId} data-id="element-1317" />}
    </div>;
};
export default MentorGoals;